// Shared display labels + ordering for the 10 org departments.
// Mirrors the OrgDepartment enum in prisma/schema.prisma.

export type Department =
  | 'sales_bookings'
  | 'finance_accounts'
  | 'legal_contracts'
  | 'marketing_pr'
  | 'fan_relations'
  | 'technical_production'
  | 'hr_people'
  | 'management_executive'
  | 'automated_transactional'
  | 'unclassified'

export interface DepartmentMeta {
  value: Department
  label: string
  description: string
  /** Internal-only departments don't appear as routing targets in some UIs. */
  internal?: boolean
}

export const DEPARTMENTS: DepartmentMeta[] = [
  {
    value: 'sales_bookings',
    label: 'Sales / Bookings',
    description: 'New enquiries, booking requests, availability checks, fee negotiations',
  },
  {
    value: 'finance_accounts',
    label: 'Finance / Accounts',
    description: 'Invoices, payment queries, contract disputes, deposit schedules',
  },
  {
    value: 'legal_contracts',
    label: 'Legal / Contracts',
    description: 'Rider review, contract amendments, licensing, IP, image rights, NDAs',
  },
  {
    value: 'marketing_pr',
    label: 'Marketing / PR',
    description: 'Press enquiries, interviews, photo/video licensing, social media collaboration',
  },
  {
    value: 'fan_relations',
    label: 'Fan / Artist Relations',
    description: 'Fan mail, personal messages, meet-and-greet requests',
  },
  {
    value: 'technical_production',
    label: 'Technical / Production',
    description: 'Stage specs, AV requirements, technical riders, backline queries',
  },
  {
    value: 'hr_people',
    label: 'HR / People',
    description: 'Crew applications, employment, contractor onboarding, grievances',
  },
  {
    value: 'management_executive',
    label: 'Management / Executive',
    description: 'Principal, manager, confidential matters that fit nowhere else',
  },
  {
    value: 'automated_transactional',
    label: 'Automated / Transactional',
    description: 'Receipts, system notifications — ingested to Finance, no human emailed',
    internal: true,
  },
  {
    value: 'unclassified',
    label: 'Unclassified / Review',
    description: "Low-confidence catch-all — reviewed periodically by a human",
    internal: true,
  },
]

export const DEPARTMENT_BY_VALUE: Record<Department, DepartmentMeta> = Object.fromEntries(
  DEPARTMENTS.map((d) => [d.value, d]),
) as Record<Department, DepartmentMeta>

/** Human departments only (excludes automated_transactional + unclassified). */
export const HUMAN_DEPARTMENTS = DEPARTMENTS.filter((d) => !d.internal)
